import "typeface-work-sans";
import "typeface-roboto";
import { COMMON_PALETTE } from "@limitless/common/utils/CommonPalette";
import { logoPng, logoWebp, loginBackgroundPng, loginBackgroundWebp, loginBackground40OpacityPng, loginBackground40OpacityWebp, invertLogoPng, invertLogoWebp, linesPng, paperPng, signinBackground } from "@limitless/images";

/**
 * The Theme class overrides the default theme from Material UI
 *
 * The variable velow represents all of our overrides.
 *
 * A full list of variables that can be overriden can be found here:
 * https://material-ui-next.com/customization/default-theme/
 *
 */

const WORK_SANS_FONT_FACE = "'Work Sans', Helvetica, Arial, sans-serif";
const WORK_SANS_SMALL_LINE_SPACE = "-0.512px";
const TABLE_COLUMN = {
  color: "#616161",
  fontWeight: 600,
  fontSize: "0.8125rem",
  fontSmoothing: "antialiased",
  letterSpacing: 0
};
export const defaultTheme = {
  assets: {
    loginBackgroundImg: loginBackgroundPng,
    webpLoginBackgroundImg: loginBackgroundWebp,
    loginBackgroundImg40Opacity: loginBackground40OpacityPng,
    webpLoginBackgroundImg40Opacity: loginBackground40OpacityWebp,
    loginLogo: logoPng,
    webploginLogo: logoWebp,
    logoImg: invertLogoPng,
    webpLogoImg: invertLogoWebp,
    linesPng,
    paperPng,
    signinBackground
  },
  palette: {
    ...COMMON_PALETTE,
    border: "1px solid rgba(163,168,168, 0.5)",
    seperator: "1px solid rgba(121,129,147, 0.33)",
    boldSeperator: "1px solid rgba(121,129,147)",
    thickSeperator: "3px solid #F0F1F4",
    rightBoxShadow: "0px 0px 5px 0px rgba(0,0,0,0.3)"
  },
  background: {
    default: "#f4f4f4",
    paper: "#fff",
    appBar: "#f5f5f5",
    contentFrame: "rgba(214,215,214,0.25)",
    dark: "#979EB4",
    gray: "#EAECF0",
    linear: "linear-gradient(to right, #c9daff 10%, #e9f0ff 50%, #e9f0ff 50%, #e9f0ff 50%)"
  },
  login: {
    background: "rgba(97,97,97,0.7)",
    contrastText: "#f7f6f6"
  },
  typography: {
    htmlFontSize: 16,
    fontFamily: "'Roboto', Helvetica, Arial, sans-serif",
    fontSize: 14,
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 700,
    h1: {
      fontFamily: WORK_SANS_FONT_FACE,
      letterSpacing: 0.25,
      fontSize: "2.25rem",
      //102
      fontWeight: 400,
      lineHeight: 1.2
    },
    h2: {
      fontFamily: WORK_SANS_FONT_FACE,
      fontSize: "1.5625rem",
      //25
      fontWeight: 400,
      lineHeight: 1.2
    },
    h3: {
      fontSize: "1.9375rem",
      //31
      fontWeight: 400,
      lineHeight: 1.2,
      letterSpacing: 0.25
    },
    h4: {
      fontSize: "1.5rem",
      //24
      fontWeight: 400,
      letterSpacing: 0.25,
      lineHeight: 1.2
    },
    h5: {
      fontSize: "1.25rem",
      //20
      fontWeight: 500,
      letterSpacing: 0.15,
      lineHeight: 1.2
    },
    h6: {
      fontSize: "1rem",
      //16
      fontWeight: 500,
      letterSpacing: 0.2,
      lineHeight: 1.2
    },
    subtitle1: {
      fontWeight: 400,
      letterSpacing: 0.15,
      lineHeight: 1.2
    },
    subtitle2: {
      fontWeight: 500,
      letterSpacing: 0.15,
      lineHeight: 1.2
    },
    caption: {
      fontWeight: 400,
      letterSpacing: 0.4,
      lineHeight: 1.2
    },
    body1: {
      fontSize: "0.8125rem",
      //13
      fontWeight: 400,
      letterSpacing: 0
    },
    body2: {
      fontSize: "0.875rem",
      //14
      fontWeight: 400,
      letterSpacing: 0,
      lineHeight: 1.2
    },
    button: {
      fontSize: "0.875rem",
      //14
      fontWeight: 500,
      letterSpacing: 0.2
    },
    navItem: {
      fontFamily: `${WORK_SANS_FONT_FACE} !important`,
      letterSpacing: `${WORK_SANS_SMALL_LINE_SPACE} !important` //important due to bug in translation context
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 480,
      md: 768,
      lg: 1024,
      xl: 1440
    }
  },
  grid: {
    gridTemplateContainer: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gridAutoFlow: "row",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      gap: "0px 0px",
      minWidth: 370
    }
  },
  components: {
    MUIDataTableBody: {
      styleOverrides: {
        emptyTitle: {
          color: "rgba(0, 0, 0, 0.87)"
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          marginTop: "initial",
          marginBottom: "initial"
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          paddingTop: 11,
          paddingBottom: 11
        },
        gutters: {
          paddingLeft: 24
        },
        button: {
          "&:hover": {
            backgroundColor: "#D3E1FF"
          }
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "initial"
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
          fontSmoothing: "antialiased"
        },
        textPrimary: {
          "&:hover": {
            backgroundColor: "rgba(66, 165, 245, 0.30)"
          }
        },
        text: {
          padding: "8px 16px",
          textTransform: "none",
          letterSpacing: 0.2
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 0
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          height: 48
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "4px 15px 4px 15px"
        },
        body: {
          color: "#616161",
          fontWeight: 400,
          wordBreak: "break-word"
        },
        head: TABLE_COLUMN
      }
    },
    MUIDataTableSelectCell: {
      styleOverrides: {
        checkboxRoot: {
          color: "rgba(0,79,265,0.8) !important" //MUI DataTable bug :(
        },
        fixedHeader: {
          backgroundColor: "#fff"
        }
      }
    },
    MUIDataTable: {
      styleOverrides: {
        responsiveScroll: {
          maxHeight: "calc(100vh - 380px)"
        }
      }
    },
    MUIDataTableToolbar: {
      styleOverrides: {
        actions: {
          flex: "unset",
          paddingTop: 12
        },
        icon: {
          "&:hover": {
            color: "rgba(0,79,265,0.8)"
          }
        },
        root: {
          paddingLeft: 16,
          paddingRight: 16
        },
        titleText: {
          fontSize: "0.9375rem",
          paddingTop: 16
        }
      }
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          verticalAlign: "top",
          lineHeight: "2.1rem"
        },
        data: TABLE_COLUMN,
        toolButton: {
          paddingLeft: 0,
          marginLeft: 0,
          "&:hover": {
            backgroundColor: "transparent"
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: 12
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontSmoothing: "antialiased"
        }
      },
      defaultProps: {
        variant: "body2"
      }
    },
    MUIDataTableFilter: {
      styleOverrides: {
        root: {
          minWidth: 350
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: "initial",
          fontWeight: 500,
          fontSmoothing: "antialiased"
        }
      }
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          display: "flex",
          padding: 24
        }
      }
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          "&.Mui-active": {
            color: "rgba(0,79,265,0.8) !important" //MUI bug :(
          },
          "&.Mui-completed": {
            color: "rgba(0,79,265,0.8) !important" //MUI bug :(
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        label: {
          fontSize: "0.6875rem"
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingBottom: 0
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        spacing: {
          marginLeft: 16,
          "& > * + *": {
            marginLeft: 16
          }
        }
      }
    },
    MUIDataTableToolbarSelect: {
      styleOverrides: {
        root: {
          height: 48
        }
      }
    },
    MuiLink: {
      defaultProps: {
        underline: "hover"
      }
    }
  }
};